import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Shield, ShieldCheck, ShieldOff, Smartphone, Key, Copy, Check, 
  AlertTriangle, Loader2, ArrowLeft, Eye, EyeOff, RefreshCw,
  Clock, MapPin, Monitor, CheckCircle2, XCircle, Lock, Unlock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'if_auth_token';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const SecuritySettings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  // MFA State
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Login History State
  const [loginHistory, setLoginHistory] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState(null);

  useEffect(() => {
    // Wait for auth context to finish loading
    if (authLoading) return;
    
    // Check if user is authenticated via either user object or token
    const token = localStorage.getItem(TOKEN_KEY);
    
    if (!user && !token) {
      // Store where user wanted to go
      localStorage.setItem('redirect_after_auth', '/settings/security');
      navigate('/login');
      return;
    }
    
    // User is authenticated (either via context or token)
    fetchSecurityData();
  }, [user, authLoading, navigate]);

  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const [mfaRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/mfa/status`, { withCredentials: true, headers }),
        axios.get(`${BACKEND_URL}/api/auth/login-history`, { withCredentials: true, headers })
      ]);
      
      setMfaStatus(mfaRes.data);
      setLoginHistory(historyRes.data.attempts || []);
    } catch (error) {
      console.error('Error fetching security data:', error);
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        localStorage.setItem('redirect_after_auth', '/settings/security');
        navigate('/login');
        return;
      }
      toast({
        title: "Error",
        description: "Failed to load security settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartMFASetup = async () => {
    try {
      setSetupLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/mfa/setup`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setMfaSetupData(response.data);
        toast({
          title: "MFA Setup Started",
          description: "Scan the QR code with your authenticator app"
        });
      }
    } catch (error) {
      toast({
        title: "Setup Failed",
        description: error.response?.data?.detail || "Failed to start MFA setup",
        variant: "destructive"
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifyMFASetup = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code from your authenticator app",
        variant: "destructive"
      });
      return;
    }

    try {
      setVerifyLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/mfa/verify-setup`,
        { code: verificationCode },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast({
          title: "MFA Enabled! 🎉",
          description: "Your account is now protected with two-factor authentication"
        });
        setMfaSetupData(null);
        setVerificationCode('');
        fetchSecurityData();
      }
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error.response?.data?.detail || "Invalid code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!disableCode || disableCode.length < 6) {
      toast({
        title: "Code Required",
        description: "Enter your authenticator code to disable MFA",
        variant: "destructive"
      });
      return;
    }

    try {
      setDisableLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/mfa/disable`,
        { code: disableCode },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast({
          title: "MFA Disabled",
          description: "Two-factor authentication has been turned off"
        });
        setShowDisableModal(false);
        setDisableCode('');
        fetchSecurityData();
      }
    } catch (error) {
      toast({
        title: "Failed to Disable",
        description: error.response?.data?.detail || "Invalid code",
        variant: "destructive"
      });
    } finally {
      setDisableLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length < 6) {
      toast({
        title: "Code Required",
        description: "Enter your authenticator code to regenerate backup codes",
        variant: "destructive"
      });
      return;
    }

    try {
      setRegenerateLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/mfa/regenerate-backup-codes`,
        { code: regenerateCode },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setNewBackupCodes(response.data.backup_codes);
        setRegenerateCode('');
        toast({
          title: "New Backup Codes Generated",
          description: "Save these codes securely - they won't be shown again!"
        });
        fetchSecurityData();
      }
    } catch (error) {
      toast({
        title: "Failed to Regenerate",
        description: error.response?.data?.detail || "Invalid code",
        variant: "destructive"
      });
    } finally {
      setRegenerateLoading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyAllCodes = (codes) => {
    navigator.clipboard.writeText(codes.join('\n'));
    toast({
      title: "Copied!",
      description: "All backup codes copied to clipboard"
    });
  };

  // Show loading while auth is being checked
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/profile')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-purple-600" />
              Security Settings
            </h1>
            <p className="text-slate-600">Manage your account security and authentication</p>
          </div>
        </div>

        {/* MFA Section */}
        <Card className="mb-6 border-2 border-purple-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                  Two-Factor Authentication (2FA)
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </div>
              {mfaStatus?.mfa_enabled ? (
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <ShieldCheck className="w-4 h-4 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                  <ShieldOff className="w-4 h-4 mr-1" />
                  Disabled
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {!mfaStatus?.mfa_enabled ? (
              // MFA Not Enabled - Show Setup
              !mfaSetupData ? (
                <div className="bg-purple-50 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Protect Your Account
                  </h3>
                  <p className="text-slate-600 mb-4 max-w-md mx-auto">
                    Two-factor authentication adds an extra layer of security. 
                    You'll need your phone to sign in.
                  </p>
                  <Button 
                    onClick={handleStartMFASetup}
                    disabled={setupLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {setupLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
                    ) : (
                      <><ShieldCheck className="w-4 h-4 mr-2" /> Enable 2FA</>
                    )}
                  </Button>
                </div>
              ) : (
                // MFA Setup In Progress
                <div className="space-y-6">
                  {/* Step 1: QR Code */}
                  <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                      Scan QR Code
                    </h4>
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                      <div className="bg-white p-4 rounded-lg border">
                        <img 
                          src={mfaSetupData.qr_code} 
                          alt="MFA QR Code" 
                          className="w-48 h-48"
                        />
                      </div>
                      <div className="flex-1 space-y-3">
                        <p className="text-slate-600">
                          Scan this QR code with your authenticator app:
                        </p>
                        <ul className="text-sm text-slate-600 space-y-1">
                          <li>• Google Authenticator</li>
                          <li>• Authy</li>
                          <li>• Microsoft Authenticator</li>
                          <li>• 1Password</li>
                        </ul>
                        <div className="pt-2">
                          <p className="text-xs text-slate-500 mb-1">Or enter this key manually:</p>
                          <code className="bg-slate-100 px-3 py-1 rounded text-sm font-mono">
                            {mfaSetupData.manual_key}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Backup Codes */}
                  <div className="bg-white border-2 border-amber-200 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm">2</span>
                      Save Backup Codes
                      <Badge className="bg-amber-100 text-amber-800">Important!</Badge>
                    </h4>
                    <p className="text-slate-600 mb-4">
                      Save these backup codes in a secure place. You can use them to access your account if you lose your phone.
                    </p>
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-2 gap-2">
                        {mfaSetupData.backup_codes.map((code, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between bg-white px-3 py-2 rounded border font-mono text-sm"
                          >
                            <span>{code}</span>
                            <button 
                              onClick={() => copyToClipboard(code, idx)}
                              className="text-slate-400 hover:text-purple-600"
                            >
                              {copiedCode === idx ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyAllCodes(mfaSetupData.backup_codes)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy All Codes
                    </Button>
                  </div>

                  {/* Step 3: Verify */}
                  <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                      Verify Setup
                    </h4>
                    <p className="text-slate-600 mb-4">
                      Enter the 6-digit code from your authenticator app to complete setup:
                    </p>
                    <div className="flex gap-3">
                      <Input
                        type="text"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-40 text-center text-lg font-mono tracking-widest"
                        maxLength={6}
                      />
                      <Button 
                        onClick={handleVerifyMFASetup}
                        disabled={verifyLoading || verificationCode.length < 6}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {verifyLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><CheckCircle2 className="w-4 h-4 mr-2" /> Verify & Enable</>
                        )}
                      </Button>
                    </div>
                  </div>

                  <Button 
                    variant="ghost" 
                    onClick={() => setMfaSetupData(null)}
                    className="text-slate-600"
                  >
                    Cancel Setup
                  </Button>
                </div>
              )
            ) : (
              // MFA Enabled - Show Status & Options
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                  <ShieldCheck className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900">
                      Two-factor authentication is active
                    </p>
                    <p className="text-sm text-green-700">
                      Your account is protected with an authenticator app.
                      {mfaStatus.enabled_at && (
                        <span className="block mt-1 text-green-600">
                          Enabled on {new Date(mfaStatus.enabled_at).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => setShowRegenerateModal(true)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    New Backup Codes ({mfaStatus.backup_codes_remaining} left)
                  </Button>
                  <Button 
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setShowDisableModal(true)}
                  >
                    <ShieldOff className="w-4 h-4 mr-2" />
                    Disable 2FA
                  </Button>
                </div>

                {/* New Backup Codes Display */}
                {newBackupCodes && (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                    <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                      <Key className="w-5 h-5" />
                      Your New Backup Codes
                    </h4>
                    <p className="text-sm text-amber-700 mb-3">
                      Save these codes now! They won't be shown again.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {newBackupCodes.map((code, idx) => (
                        <code key={idx} className="bg-white px-3 py-2 rounded border font-mono text-sm">
                          {code}
                        </code>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyAllCodes(newBackupCodes)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy All
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setNewBackupCodes(null);
                          setShowRegenerateModal(false);
                        }}
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Login History Section */}
        <Card className="border-2 border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-600" />
              Recent Login Activity
            </CardTitle>
            <CardDescription>
              Review recent sign-in attempts to your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loginHistory.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No login history available</p>
            ) : (
              <div className="space-y-3">
                {loginHistory.slice(0, 10).map((attempt, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      attempt.success 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {attempt.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {attempt.success ? 'Successful login' : 'Failed attempt'}
                          {attempt.failure_reason && (
                            <span className="text-red-600 ml-2">({attempt.failure_reason})</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {attempt.ip_address}
                          {attempt.user_agent && (
                            <>
                              <Monitor className="w-3 h-3 ml-2" />
                              {attempt.user_agent.substring(0, 50)}...
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(attempt.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Disable MFA Modal */}
        {showDisableModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Disable Two-Factor Authentication
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600">
                  This will remove the extra security from your account. Enter your authenticator code to confirm:
                </p>
                <Input
                  type="text"
                  placeholder="000000"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-lg font-mono tracking-widest"
                  maxLength={6}
                />
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowDisableModal(false);
                      setDisableCode('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={handleDisableMFA}
                    disabled={disableLoading || disableCode.length < 6}
                  >
                    {disableLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Disable 2FA'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Regenerate Backup Codes Modal */}
        {showRegenerateModal && !newBackupCodes && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                  Generate New Backup Codes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600">
                  This will invalidate your old backup codes. Enter your authenticator code to confirm:
                </p>
                <Input
                  type="text"
                  placeholder="000000"
                  value={regenerateCode}
                  onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-lg font-mono tracking-widest"
                  maxLength={6}
                />
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowRegenerateModal(false);
                      setRegenerateCode('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={handleRegenerateBackupCodes}
                    disabled={regenerateLoading || regenerateCode.length < 6}
                  >
                    {regenerateLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Generate Codes'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
